import { routes } from '@/shared/routes';
import { csrfToken } from '@/shared/utils/csrf';

export type OAuthProvider = 'google_oauth2' | 'microsoft_graph' | 'facebook';

type OAuthFlow = 'popup' | 'redirect';

export type OAuthFailure = 'popup_blocked' | 'popup_closed' | 'timeout' | 'stale_session' | 'unknown';

export class OAuthError extends Error {
  constructor(readonly failure: OAuthFailure) {
    super(failure);
    this.name = 'OAuthError';
  }
}

export interface OAuthIdentity {
  email: string;
  name: string;
  provider: OAuthProvider;
  uid: string;
  oauth_token: string;
}

export type OAuthResult =
  | { status: 'authenticated' }
  | { status: 'needs_signup'; identity: OAuthIdentity };

interface OAuthPopupMessage {
  type: 'oauth_success';
  status: OAuthResult['status'];
  identity?: OAuthIdentity;
}

const OAUTH_TIMEOUT_MS = 5 * 60 * 1000;
const POPUP_WINDOW_NAME = 'oauth_popup';
const REQUEST_TARGETS: Record<OAuthFlow, string> = {
  popup: POPUP_WINDOW_NAME,
  redirect: '_self',
};

export function redirectToOAuth(provider: OAuthProvider): void {
  submitOnce(oauthRequestForm(provider, 'redirect'));
}

export function openOAuthPopup(provider: OAuthProvider): Promise<OAuthResult> {
  return new Promise((resolve, reject) => {
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const form = oauthRequestForm(provider, 'popup');

    const popup = window.open(
      '',
      POPUP_WINDOW_NAME,
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );

    if (!popup || popup.closed) {
      reject(new OAuthError('popup_blocked'));
      return;
    }

    submitOnce(form);

    let cleaned = false;

    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      window.removeEventListener('message', handleMessage);
      clearInterval(checkClosed);
      clearTimeout(timeoutId);
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as Partial<OAuthPopupMessage> | null;
      if (data?.type !== 'oauth_success') return;

      cleanup();
      if (data.status === 'needs_signup' && data.identity) {
        resolve({ status: 'needs_signup', identity: data.identity });
      } else {
        resolve({ status: 'authenticated' });
      }
    };

    const checkClosed = setInterval(() => {
      if (popup.closed) {
        cleanup();
        reject(new OAuthError('popup_closed'));
      }
    }, 500);

    const timeoutId = setTimeout(() => {
      cleanup();
      popup.close();
      reject(new OAuthError('timeout'));
    }, OAUTH_TIMEOUT_MS);

    window.addEventListener('message', handleMessage);
  });
}

function oauthRequestForm(provider: OAuthProvider, flow: OAuthFlow): HTMLFormElement {
  const token = csrfToken();
  if (!token) throw new OAuthError('stale_session');

  const form = document.createElement('form');
  form.method = 'post';
  form.action = routes.auth.oauthRequest(provider);
  form.target = REQUEST_TARGETS[flow];
  form.append(hiddenField('origin', flow), hiddenField('authenticity_token', token));

  return form;
}

function submitOnce(form: HTMLFormElement): void {
  document.body.append(form);
  form.submit();
  form.remove();
}

function hiddenField(name: string, value: string): HTMLInputElement {
  const field = document.createElement('input');
  field.type = 'hidden';
  field.name = name;
  field.value = value;
  return field;
}
